import { Avatar, Badge, Box, Button, Card, Group, Tabs, Text, Title } from "@mantine/core";
import { useState } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { BillingTenantApi } from "../../../apis";
import { ContractStatusBadge } from "../../../components/billing/ContractStatusBadge";
import { PageLayout } from "../../../components/layout";
import { TenantOverviewTab } from "./tabs/overview";
import { TenantContractTab } from "./tabs/charge-contract";
import { TenantFeePoliciesTab } from "./tabs/fee-policies";
import { TenantInvoicesTab } from "./tabs/invoices";

const REVENUE_MODEL_LABEL: Record<string, string> = {
  PLATFORM_SUBSCRIPTION: "Платформ захиалга",
  TENANT_FEE_BASED     : "Тенант шимтгэл",
  HYBRID               : "Хосолсон",
};

const headerWash = { background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))" };

const TenantBillingDetail = () => {
  const { tenantId = "" } = useParams();
  // Source the header from the billing aggregate (carries revenueModel + status);
  // the theme service does not. No by-id detail route exists yet — see UI-SPEC §5.
  const { data } = useSWR("swr.billing.tenants.aggregate", () => BillingTenantApi.listBillingTenants({ limit: 100 }));
  const tenant = data?.rows.find((t) => t.id === tenantId);
  const name = tenant?.name ?? tenantId;
  // Subtitle shows the tenant ID (mockup §2.2 header).
  const subTitle = tenantId;
  const [activeTab, setActiveTab] = useState<string | null>("overview");
  const breadcrumb = [
    { to: "/", label: "Эхлэл" },
    { to: "/billing/tenants", label: "Тенантууд" },
    { label: name },
  ];

  return (
    <PageLayout title={name} subTitle={subTitle} breadcrumb={breadcrumb}>
      <Card withBorder radius="md" padding="lg" mb="md" style={headerWash}>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Group gap="md" wrap="nowrap">
            <Avatar color="brand" radius="md" size={48}>{(name?.[0] ?? "?").toUpperCase()}</Avatar>
            <Box>
              <Title order={3}>{name}</Title>
              <Group gap="xs" mt={2}>
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: "tabular-nums" }}>{tenantId}</Text>
                {tenant?.domain && (
                  <>
                    <Text size="xs" c="dimmed">·</Text>
                    <Text size="xs" c="dimmed">{tenant.domain}</Text>
                  </>
                )}
              </Group>
            </Box>
          </Group>
          <Group gap="sm" align="center">
            {tenant?.revenueModel && (
              <Badge color="gray" variant="light" radius="sm">
                {REVENUE_MODEL_LABEL[tenant.revenueModel] ?? tenant.revenueModel}
              </Badge>
            )}
            {/* TODO: tenant status enum may diverge from ChargeContract status — confirm or split into TenantStatusBadge */}
            {tenant?.status && (
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              <ContractStatusBadge status={tenant.status as any} />
            )}
            <Button variant="subtle" size="xs" color="brand" onClick={() => alert("Удахгүй")}>Revenue model солих</Button>
          </Group>
        </Group>
      </Card>
      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="overview">Тойм</Tabs.Tab>
          <Tabs.Tab value="contract">Гэрээ</Tabs.Tab>
          <Tabs.Tab value="fees">
            Шимтгэлийн бодлого <Badge size="xs" color="gray" variant="light">read-only</Badge>
          </Tabs.Tab>
          <Tabs.Tab value="invoices">Нэхэмжлэх</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="overview" pt="md">
          <TenantOverviewTab tenantId={tenantId} onOpenContract={() => setActiveTab("contract")} />
        </Tabs.Panel>
        <Tabs.Panel value="contract" pt="md"><TenantContractTab tenantId={tenantId} /></Tabs.Panel>
        <Tabs.Panel value="fees" pt="md"><TenantFeePoliciesTab tenantId={tenantId} /></Tabs.Panel>
        <Tabs.Panel value="invoices" pt="md"><TenantInvoicesTab tenantId={tenantId} /></Tabs.Panel>
      </Tabs>
    </PageLayout>
  );
};

export default TenantBillingDetail;
